import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { isBefore, startOfDay } from 'date-fns';
import { first } from 'lodash';

import { feSidebarOpen } from 'actions/ui.actions';
import SalonFlag from 'components/core/SalonFlag.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import Space from 'components/generic/Space.component';
import COLOR_NAME from 'constants/colors.const';
import RECURRENCE_DESCRIPTION from 'constants/recurrence/recurrenceDescription.const';
import RRPT from 'constants/recurrence/recurrenceRuleParseType.const';
import SF from 'constants/salonFlag.const';
import S from 'constants/string.const';
import SP from 'constants/ui/sidePanel.const';
import { useNavFor, useNavIsWeek } from 'hooks/useNav.hook';
import useOnClickOutside from 'hooks/useOnClickOutsideRef.hook';
import useTip from 'hooks/useTip.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonId, selectCurrentSalonStylists } from 'selectors/currentSalon.selectors';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { formatHourAndMinuteShort, formatPartTime, formatSegmentWeek, formatYearToDayCompact } from 'utils/date/format.util';
import { nowAsZulu } from 'utils/date/now.util';
import elementScrollTo from 'utils/dom/elementScrollTo.util';
import call from 'utils/functions/call.util';
import asObject from 'utils/objects/asObject.util';
import deRef from 'utils/objects/deRef.util';
import byId from 'utils/predicates/byId.util';
import isNotDomElement from 'utils/predicates/dom/isNotDomElement.util';
import isFullString from 'utils/predicates/string/isFullString.util';
import recurrenceRuleSimplified from 'utils/recurrence/recurrenceRuleSimplified.util';

import CN from './EventModalTimeOff.module.scss';

const CTAG = 'EventModalTimeOff';

const DEFAULT_COLOR = '#bebebe';
const USE_EDIT_CATEGORY = true;
const USE_OLD_EDIT = false;

const positionElement = (element) => {
  if (isNotDomElement(element)) return;

  const { innerWidth } = window;

  const { style } = element;
  const halfWidth = innerWidth / 2;
  const modalWidth = 320;

  if (innerWidth > 600) {
    // For tablets and desktop
    style.left = `${halfWidth - modalWidth}px`;
  } else if (innerWidth <= 320) {
    // For mobile old phone
    style.left = '10px';
    style.marginRight = '0'; // overwrite the global css
    style.marginLeft = '30px'; // overwrite the global css
  } else if (innerWidth <= 360) {
    style.left = '50px';
    style.marginRight = '0';
    style.marginLeft = '0';
  } else {
    // For other mobile phones
    style.left = '80px';
    style.marginLeft = '0';
  }

  style.position = 'fixed';
  style.top = '26%';
  style.right = 'auto';

  elementScrollTo({ element, behavior: 'instant', block: 'center', inline: 'center' });
};

const EventModalTimeOff = (props) => {
  const { tag: tagFromProps, onClose, onDeleteEvent, event, dayView, isDarkMode } = props;
  const { t } = useTranslation();
  const { formatDate } = useGilt();
  const dispatch = useDispatch();
  const salonId = useSelector(selectCurrentSalonId);
  const stylistList = useSelector(selectCurrentSalonStylists);
  const nav = useNavFor('EventModalTimeOff');
  const isWeek = useNavIsWeek();

  const tag = fromAnyToTrimmed(tagFromProps);
  const FTAG = `${tag}/${CTAG}`;

  const {
    id: eventId,
    stylist_id: stylistId,
    start_time: bgn,
    end_time: end,
    time_off_category: timeOffCategory,
    created_by: createdBy,
    date_created: dateCreated,
    recurrences,
    notes,
  } = asObject(event);

  const { val: isNoteCollapsed, tip: toggleNoteCollapsed } = useTip(false);
  const { val: isViewMoreRendered, tip: toggleViewMoreRendered } = useTip(false);
  const { val: isDeleteMessageRendered, yay: renderDeleteMessage } = useTip(false);

  const componentRef = useRef(null);
  const notesRef = useRef(null);

  const searchString = 'UNTIL';
  const isRepeatForever = recurrences && recurrences.includes(searchString);

  const regex = new RegExp(`${searchString}=([0-9TZ:]+)`);
  const [, repeatEndDate] = (recurrences && recurrences.match(regex)) || [];

  const timeDiffInMilliseconds = Math.abs(new Date(end) - new Date(bgn));
  const hours = timeDiffInMilliseconds / (1000 * 60 * 60);
  const isAllDayEvent = hours >= 8;

  const hasNotes = fromAnyToBool(asObject(notes).length > 0);

  const isRepeat = isFullString(recurrences);
  const rrs = recurrenceRuleSimplified({ string: recurrences, type: RRPT.mm });
  const displayRecurrence = t(RECURRENCE_DESCRIPTION[rrs]) || t('scheduler.scheduleEvent.repeats');

  const displayDate = formatDate(bgn, { hideTime: true });
  const displayEndHour = formatPartTime(end);
  const displayBgnHourShort = formatHourAndMinuteShort(bgn);

  const { color: categoryColor, name: categoryName } = asObject(timeOffCategory);
  const backgroundColor = categoryColor || DEFAULT_COLOR;
  const headerStyle = useMemo(() => ({ backgroundColor }), [backgroundColor]);

  const filteredStylists = stylistList.filter(byId(stylistId));
  const { user_name: userName } = asObject(first(filteredStylists));

  useOnClickOutside(componentRef, onClose);

  const toggleNotes = useCallback(
    (element) => {
      const TWO_LINES_IN_PIXELS = 36;

      if (element && !isViewMoreRendered) {
        const height = element.clientHeight;

        if (height > TWO_LINES_IN_PIXELS) {
          toggleNoteCollapsed();
          toggleViewMoreRendered(); // mark, that toggleNotes is called
        }
      }
    },
    [isViewMoreRendered, toggleNoteCollapsed, toggleViewMoreRendered],
  );

  const handleEdit = useCallback(() => {
    call(`${FTAG}.onClose`, onClose);

    if (!eventId) return;

    if (USE_OLD_EDIT) {
      nav.push(
        !dayView && isWeek
          ? `/salon/${salonId}/week/${formatSegmentWeek(bgn)}/${stylistId}/event/edit/${eventId}/load`
          : `/salon/${salonId}/day/${formatYearToDayCompact(bgn)}/event/edit/${eventId}/load`,
      );
      return;
    }

    dispatch(feSidebarOpen({ panel: SP.timeOffCat, salonId, stylistId, event }));
  }, [FTAG, onClose, eventId, dispatch, salonId, stylistId, event, nav, dayView, isWeek, bgn]);

  const handleDelete = useCallback(() => {
    call(`${FTAG}.onClose`, onClose);
    call(`${FTAG}.onDeleteEvent`, onDeleteEvent, { event, all: false });
  }, [FTAG, event, onClose, onDeleteEvent]);

  const handleDeleteAll = useCallback(() => {
    call(`${FTAG}.onClose`, onClose);
    call(`${FTAG}.onDeleteEvent`, onDeleteEvent, { event, all: true });
  }, [FTAG, event, onClose, onDeleteEvent]);

  useEffect(() => {
    toggleNotes(deRef(notesRef));
    positionElement(deRef(componentRef));
  }, [toggleNotes]);

  const componentClasses = cn({
    'card-modal-wraper animated zoomIn': true,
    [CN.component]: true,
    [CN.dark]: isDarkMode,
  });

  const headerClasses = cn({
    [CN.modalHeader]: true,
    [CN.darkFg]: [COLOR_NAME.greenLight, COLOR_NAME.purpleLight, COLOR_NAME.pinkLight, COLOR_NAME.grayLight, COLOR_NAME.blueLight, COLOR_NAME.yellow, COLOR_NAME.orange].includes(categoryColor),
  });

  const now = nowAsZulu();
  const startDate = new Date(bgn);
  const isPastTimeOffEvent = isBefore(startDate, startOfDay(now));

  return (
    <div data-bem="EventModalTimeOff">
      <div data-bem="EventModalTimeOff__overlay" className={CN.overlay} role="button" tabIndex={0} onClick={onClose} />
      <div data-bem="EventModalTimeOff__inside" ref={componentRef} className={componentClasses}>
        <div data-bem="EventModalTimeOff__header" className={headerClasses} style={headerStyle}>
          <div data-bem="EventModalTimeOff__user-name" className={CN.stylistName}>
            {userName}
          </div>
          <div data-bem="EventModalTimeOff__category-name" className={CN.timeOffCategory}>
            {categoryName}
          </div>

          <div data-bem="EventModalTimeOff__close-button" className={CN.closeButton}>
            <Button variant="empty" onClick={onClose}>
              <Icon variant="close" />
            </Button>
          </div>
        </div>

        <div data-bem="EventModalTimeOff__body" className={CN.modalBody}>
          <div data-bem="EventModalTimeOff__calendar-line" className={CN.info}>
            <div data-bem="EventModalTimeOff__calendar-icon" className={CN.icon}>
              <Icon variant="calendar" />
            </div>
            <div data-bem="EventModalTimeOff__calendar-body" className={CN.text}>
              {displayDate}
              {isAllDayEvent ? (
                <>
                  <Space>{S.minus}</Space>
                  {t('scheduler.scheduleEvent.allDay')}
                </>
              ) : (
                <Space>
                  , {displayBgnHourShort}-{displayEndHour}
                </Space>
              )}
            </div>
          </div>

          <div data-bem="EventModalTimeOff__recurrence-line" className={CN.info}>
            <div data-bem="EventModalTimeOff__recurrence-icon" className={CN.icon}>
              <Icon variant="repeat" />
            </div>
            <div data-bem="EventModalTimeOff__recurrence-body" className={CN.text}>
              {displayRecurrence}
            </div>
          </div>

          {isRepeat && (
            <div data-bem="EventModalTimeOff__ends-line" className={CN.info}>
              <div data-bem="EventModalTimeOff__ends-icon" className={CN.icon}>
                <Icon variant="repeat" />
              </div>
              <div data-bem="EventModalTimeOff__ends-body" className={CN.text}>
                <Space>
                  {t('scheduler.scheduleEvent.repeatEnds')} {S.minus}
                </Space>
                {!isRepeatForever && t('scheduler.scheduleEvent.never')}
                {repeatEndDate && formatDate(repeatEndDate, { hideTime: true })}
              </div>
            </div>
          )}

          <div
            data-bem="EventModalTimeOff__note-line"
            className={cn({
              [CN.noteContainer]: true,
              [CN.hasNotes]: hasNotes,
            })}
          >
            <div data-bem="EventModalTimeOff__note-icon" className={CN.iconPen}>
              <Icon variant="note" />
            </div>

            <div data-bem="EventModalTimeOff__note-body" className={CN.text}>
              <div
                data-bem="EventModalTimeOff__note-text"
                ref={notesRef}
                className={cn({
                  [CN.notes]: hasNotes,
                  [CN.isClosed]: isNoteCollapsed,
                })}
              >
                {notes}
              </div>
              <div data-bem="EventModalTimeOff__note-buttons" className={CN.flex}>
                <Button tag="EventModalTimeOff__add-edit-note" variant="slim" onClick={handleEdit}>
                  {hasNotes ? t('scheduler.scheduleEvent.editButton') : t('scheduler.scheduleEvent.addNoteButton')}
                </Button>
                {hasNotes && isViewMoreRendered && (
                  <Button tag="EventModalTimeOff__toggle-more-less" variant="slim" width="small" onClick={toggleNoteCollapsed}>
                    {isNoteCollapsed ? t('scheduler.scheduleEvent.viewMore') : t('scheduler.scheduleEvent.viewLess')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div data-bem="EventModalTimeOff__created-by" className={CN.border}>
            <div className={CN.gap}>
              <div data-bem="EventModalTimeOff__created-by-label" className={CN.createBy}>
                {t('scheduler.scheduleEvent.createdBy')}
              </div>
              <div>{createdBy}</div>
            </div>

            <div data-bem="EventModalTimeOff__created-on" className={CN.gap}>
              <div data-bem="EventModalTimeOff__created-on-label" className={CN.createBy}>
                {t('scheduler.scheduleEvent.createdOn')}
              </div>

              <div>
                {formatDate(dateCreated, { hideTime: true })} <Space>{t('scheduler.scheduleEvent.at')}</Space>
                {formatPartTime(dateCreated).toLowerCase()}
              </div>
            </div>
          </div>
        </div>

        <div data-bem="EventModalTimeOff__footer" className={CN.modalFooter}>
          {USE_EDIT_CATEGORY && (
            <SalonFlag name={SF.timeOffCats}>
              <SalonFlag.IfEnabled>
                <Button variant="primary" onClick={handleEdit}>
                  {t('scheduler.scheduleEvent.editCategoryButton')}
                </Button>
              </SalonFlag.IfEnabled>
            </SalonFlag>
          )}

          <Button variant="warning" onClick={renderDeleteMessage}>
            <Icon variant="delete" width="md" />
            {t('common.buttons.deleteUpp')}
          </Button>

          {isDeleteMessageRendered && (
            <div className={CN.delTooltip}>
              {isPastTimeOffEvent ? (
                <span className={CN.tooltipText}>{t('scheduler.scheduleEvent.deletePopup.text1')}</span>
              ) : (
                <>
                  <span className={CN.tooltipText}>{t('scheduler.scheduleEvent.deletePopup.text2')}</span>
                  <div>
                    <Button variant="slim" width="small" onClick={onClose}>
                      {t('common.buttons.no')}
                    </Button>
                    <Button variant="primary" width="small" onClick={event ? handleDeleteAll : handleDelete}>
                      {t('common.buttons.yes')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventModalTimeOff;
